import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ImageUploader } from "@/components/ImageUploader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Youtube, Facebook, Music2 } from "lucide-react";

const socialFields = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "youtube", label: "YouTube", Icon: Youtube },
  { key: "tiktok", label: "TikTok", Icon: Music2 },
  { key: "facebook", label: "Facebook", Icon: Facebook },
];

export function SettingsDialog({ open, onOpenChange, config, onSaved }) {
  const [form, setForm] = useState(config);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(config); }, [config, open]);
  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config", form);
      toast.success("Page updated");
      onSaved(form);
      onOpenChange(false);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border border-white/15 rounded-none max-w-lg" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl">Page Settings</DialogTitle>
          <DialogDescription className="sr-only">Edit the banner, background image and social links.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="banner" className="w-full">
          <TabsList className="bg-black/40 rounded-none border border-white/10 w-full justify-start">
            <TabsTrigger value="banner" className="rounded-none data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase" data-testid="tab-banner">Banner</TabsTrigger>
            <TabsTrigger value="background" className="rounded-none data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase" data-testid="tab-background">Background</TabsTrigger>
            <TabsTrigger value="socials" className="rounded-none data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase" data-testid="tab-socials">Socials</TabsTrigger>
          </TabsList>

          <TabsContent value="banner" className="space-y-4 pt-4">
            <ImageUploader value={form.banner_image_url} onChange={(u) => set("banner_image_url", u)} label="Banner Image" testId="banner" />
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Title</Label>
              <Input data-testid="banner-title-input" value={form.banner_title} onChange={(e) => set("banner_title", e.target.value)}
                className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Subtitle</Label>
              <Input data-testid="banner-subtitle-input" value={form.banner_subtitle || ""} onChange={(e) => set("banner_subtitle", e.target.value)}
                className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" />
            </div>
          </TabsContent>

          <TabsContent value="background" className="space-y-4 pt-4">
            <ImageUploader value={form.background_image_url} onChange={(u) => set("background_image_url", u)} label="Background Image" testId="background" />
            <p className="text-xs text-zinc-500 font-mono">Tip: dark, low-contrast images work best behind the content.</p>
          </TabsContent>

          <TabsContent value="socials" className="space-y-3 pt-4">
            {socialFields.map(({ key, label, Icon }) => (
              <div key={key} className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2"><Icon size={13} /> {label}</Label>
                <Input data-testid={`social-input-${key}`} value={form.social_links?.[key] || ""} onChange={(e) => setSocial(key, e.target.value)}
                  className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" placeholder="https://... (leave empty to hide)" />
              </div>
            ))}
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <button data-testid="settings-save-btn" onClick={save} disabled={saving}
            className="bg-white text-black font-bold px-6 py-3 uppercase tracking-wider text-sm hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
